// src/pages/TablerosAdminPage.jsx
// Tableros: es la versión nueva (era "Tableros V2") y quedó como el tablero de
// la app para todos los roles. El anterior sigue vivo en /tableros-respaldo,
// dentro del menu de admin, como respaldo.
//
// El nombre del archivo quedó como estaba para no mover el componente con sus
// imports y el doc: si algún día se renombra, el que sigue siendo el tablero
// principal es este.
import React from 'react';
import { useObrasData } from '../hooks/useObrasData';
import ReactECharts from 'echarts-for-react';
import TablerosAdminFilters from '../components/dashboard/TablerosAdminFilters';
import GraficoPartidos from '../components/dashboard/GraficoPartidos';
import { GraficoTortaEstados } from '../components/dashboard/GraficoTortaEstados';
import { ComponenteMapas } from '../components/dashboard/ComponenteMapas';
import { SelectorGraficoTipoSubsidio } from '../components/dashboard/SelectorGraficoTipoSubsidio';
import { GraficoRecursosPorTipoAnual } from '../components/dashboard/GraficoRecursosPorTipoAnual';
import { GraficoSubsidiosAnual } from '../components/dashboard/GraficoSubsidiosAnual';
import { GraficoRecursosPorTipoAnualApilado } from '../components/dashboard/GraficoRecursosPorTipoAnualApilado';
import { GraficoSubsidiosAnualApilado } from '../components/dashboard/GraficoSubsidiosAnualApilado';

export const TablerosAdminPage = () => {
	const {
		metrics,
		proyectosPorPartido,
		proyectosPorPartidoBase,
		proyectosPorPrograma,
		proyectosPorEstado,
		recursosPorSubsidioAnual,
		proyectosPorSubsidioAnual,
		loading,
		isRefetching,
		error,
		filters,
		updateFilters,
		resetFilters
	} = useObrasData({
		year: 'Todos',
		criterioYear: 'pago_anticipo',
		partido: 'Todas',
		tipo: 'Todos',
		subtipo: 'Todos',
		estado: 'Todos',
		area_responsable: 'Todos',
		mes: ''
	});

	// 📊 OPCIONES ECHARTS: Torta de Programas (Estilo Anillo Moderno, Tablero V1)
	const optionTortaProgramas = {
		tooltip: {
			trigger: 'item',
			formatter: '{b}: <b>{c}</b> ({d}%)'
		},
		legend: {
			type: 'scroll',
			orient: 'vertical',
			right: '5%',
			top: 'center',
			itemGap: 12,
			textStyle: {
				color: '#4b5563',
				fontSize: 12
			},
			formatter: function (name) {
				const encontrado = proyectosPorPrograma.find(
					p => (p.programa || 'Sin Programa Asignado') === name
				);
				const cantidad = encontrado ? encontrado.cantidad : 0;
				const nombreCorto = name.length > 30 ? name.substring(0, 27) + '...' : name;
				return `${nombreCorto} (${cantidad})`;
			}
		},
		series: [
			{
				name: 'Programas',
				type: 'pie',
				radius: ['40%', '70%'],
				center: ['30%', '50%'],
				avoidLabelOverlap: true,
				minAngle: 12,
				itemStyle: {
					borderRadius: 6,
					borderColor: '#fff',
					borderWidth: 2
				},
				label: {
					show: false,
					position: 'center'
				},
				emphasis: {
					label: {
						show: true,
						fontSize: 16,
						fontWeight: 'bold',
						formatter: '{b}'
					}
				},
				labelLine: {
					show: false
				},
				data: proyectosPorPrograma.map(p => ({
					name: p.programa || 'Sin Programa Asignado',
					value: parseInt(p.cantidad || 0)
				}))
			}
		]
	};

	// 📊 OPCIONES ECHARTS: Municipios Alcanzados (Gauge estilo Tablero V1)
	const optionGauge = {
		series: [{
			type: 'gauge',
			max: 135, // Total de municipios de PBA
			startAngle: 210,
			endAngle: -30,
			progress: {
				show: true,
				width: 14,
				itemStyle: { color: '#3b82f6' } // Color azul de la barra
			},
			axisLine: {
				lineStyle: { width: 14, color: [[1, '#e5e7eb']] } // Fondo gris claro
			},
			pointer: { show: false },
			axisTick: { show: false },
			splitLine: { show: false },
			axisLabel: {
				show: true,
				distance: -20,
				formatter: function (value) {
					if (value === 0 || value === 135) return value;
					return '';
				},
				color: '#9ca3af',
				fontSize: 12
			},
			detail: {
				valueAnimation: true,
				formatter: '{value}',
				offsetCenter: [0, '10%'],
				textStyle: {
					fontSize: 42,
					fontWeight: 'bold',
					color: '#2563eb' // 🔵 Azul institucional (blue-600)
				}
			},
			data: [{ value: metrics?.municipiosAlcanzados || 0 }]
		}]
	};

	return (
		<div className="p-6">
			<div className="flex items-center gap-3 mb-2">
				<h1 className="text-2xl font-bold text-gray-800 dark:text-white">
					Tableros
				</h1>
			</div>
			<p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
				Indicadores consolidados de las obras.
			</p>

			{error && (
				<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-md text-sm text-red-700 dark:text-red-300">
					Error al cargar los datos del tablero: {error.message || String(error)}
				</div>
			)}

			{/* Filtros de año por criterio múltiple */}
			<TablerosAdminFilters
				filters={filters}
				onFilterChange={(e) => updateFilters({ [e.target.name]: e.target.value })}
				onReset={resetFilters}
				loading={loading}
				isRefetching={isRefetching}
			/>

			{/* Fila Superior de KPIs (mismo formato que Tablero V1) */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-3 xl:grid-cols-3">
				<div className="rounded-xl border bg-white p-6 shadow-sm text-center flex flex-col justify-center dark:bg-gray-800 dark:border-gray-700">
					<h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">Total Proyectos</h3>
					<p className="mt-2 text-4xl font-bold text-blue-600 dark:text-blue-400">{loading ? '…' : (metrics?.totalObras ?? 0)}</p>
				</div>

				<div className="rounded-xl border bg-white p-6 shadow-sm text-center flex flex-col justify-center dark:bg-gray-800 dark:border-gray-700">
					<h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">Monto Conveniado</h3>
					<p className="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">
						{loading ? '…' : `$${((metrics?.presupuestoTotal ?? 0) / 1000000).toFixed(2)}M`}
					</p>
				</div>

				<div className="rounded-xl border bg-white p-6 shadow-sm flex flex-col items-center dark:bg-gray-800 dark:border-gray-700">
					<h3 className="text-sm font-medium text-gray-500 uppercase mb-2 dark:text-gray-400">Municipios Alcanzados</h3>
					<div className="h-40 w-full">
						<ReactECharts option={optionGauge} style={{ height: '100%', width: '100%' }} />
					</div>
				</div>
			</div>

			{/* Gráfico de Proyectos por Partido */}
			<div className="mb-8 mt-8">
				<GraficoPartidos
					data={proyectosPorPartido}
					baseData={proyectosPorPartidoBase}
					partidoSeleccionados={filters.partido}
					loading={loading}
				/>
			</div>

			{/* Proyectos por Programa + Proyectos por Estado (Tablero V1) */}
			<div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
				<div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-[450px]">
					<h3 className="text-base font-bold text-gray-800 dark:text-white tracking-tight uppercase mb-4">Proyectos por Programa</h3>
					<div className="flex-1 w-full min-h-0">
						<ReactECharts option={optionTortaProgramas} style={{ height: '100%', width: '100%' }} notMerge={true} />
					</div>
				</div>
				<GraficoTortaEstados data={proyectosPorEstado} estadosFiltro={filters.estado} heightClass="h-[450px]" />
			</div>

			{/* Distribución Provincial de Obras + Región Metropolitana (RMBA) */}
			<div className="mb-8">
				<ComponenteMapas
					proyectosPorPartido={proyectosPorPartidoBase}
					partidoSeleccionadoId={filters.partido}
				/>
			</div>

			{/* % de Recursos Transferidos por Tipo de Subsidio por Año (desglosadas / apiladas) */}
			<div className="mb-8">
				<SelectorGraficoTipoSubsidio
					titulo="Porcentaje de recursos transferidos por Tipo de subsidio por Año"
					subtituloActivo="Distribución entre Municipios y ONGs/Universidades"
					GraficoDesglosado={GraficoRecursosPorTipoAnual}
					GraficoApilado={GraficoRecursosPorTipoAnualApilado}
					data={recursosPorSubsidioAnual}
					anioSeleccionado={filters.year}
					vistaInicial="apilado"
				/>
			</div>

			{/* Evolución del % de Proyectos por Tipo de subsidio por Año (desglosadas / apiladas) */}
			<div className="mb-8">
				<SelectorGraficoTipoSubsidio
					titulo="Evolución del Porcentaje de los Proyectos por Tipo de subsidio por Año"
					subtituloActivo="Distribución entre Municipios y ONGs/Universidades"
					GraficoDesglosado={GraficoSubsidiosAnual}
					GraficoApilado={GraficoSubsidiosAnualApilado}
					data={proyectosPorSubsidioAnual}
					anioSeleccionado={filters.year}
					vistaInicial="desglosado"
				/>
			</div>
		</div>
	);
};
